import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSupabaseWithSession } from '@/hooks/useSupabase';
import { getPrimisCollateralStore, isCollateralDate, primisCollateralSnapshotSchema } from '@/services/primisCollateralService';
import type { ProspectLoan } from '@/components/encompass/prospects/types';

export function usePrimisCollateral(loan: ProspectLoan, enabled: boolean, onLoanUpdate?: (updates: Partial<ProspectLoan>) => void) {
  const { supabase, user } = useSupabaseWithSession();
  const organizationId = loan.organization_id ?? '';
  const key = `${user?.id}:${organizationId}:${loan.id}`;
  const store = useMemo(() => getPrimisCollateralStore(supabase, user?.id ?? '', organizationId), [supabase, user?.id, organizationId]);
  const subscribe = useCallback((notify: () => void) => enabled && user?.id
    ? store.subscribe(loan.id, notify) : () => {}, [enabled, user?.id, store, loan.id]);
  const getSnapshot = useCallback(() => store.get(loan.id), [store, loan.id]);
  const state = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    if (!enabled || !user?.id) {
      return;
    }
    const parsed = primisCollateralSnapshotSchema.safeParse(loan);
    if (parsed.success) {
      store.accept(parsed.data);
    } else if (!store.get(loan.id).snapshot) {
      void store.check(loan.id);
    }
  }, [store, loan, enabled, user?.id]);

  const [draft, setDraft] = useState<{ key: string; value: string; revision: string; invalid: boolean } | null>(null);
  const source = state.snapshot?.primis_collateral_requested_date ?? '';
  const currentDraft = draft?.key === key ? draft : null;
  const value = currentDraft?.value ?? source;
  const isDirty = !!currentDraft && (currentDraft.value !== source || currentDraft.invalid);
  useEffect(() => {
    setDraft(current => current?.key === key && !current.invalid && current.value === source ? null : current);
  }, [key, source]);
  const setValue = (next: string, invalid = false) => setDraft(current => ({
    key, value: next, invalid,
    revision: current?.key === key ? current.revision : state.snapshot?.primis_collateral_requested_revision ?? '0'
  }));
  const save = async (retry = false) => {
    if (!enabled || !user?.id || !currentDraft || !isDirty || currentDraft.invalid || !isCollateralDate(value)) {
      return !isDirty;
    }
    const saved = await store.save(loan.id, currentDraft.value, currentDraft.revision, retry);
    if (saved) {
      const snapshot = store.get(loan.id).snapshot;
      if (snapshot) {
        onLoanUpdate?.(snapshot);
      }
      setDraft(current => current?.key === key && current.value === currentDraft.value ? null : current);
    }
    return saved;
  };
  return {
    ...state, value, source, isDirty, setValue, save,
    reset: () => setDraft(null), check: () => store.check(loan.id),
    blocked: !enabled || !user?.id || !state.snapshot || state.saving,
    valid: !currentDraft?.invalid && isCollateralDate(value)
  };
}
export type PrimisCollateralControl = ReturnType<typeof usePrimisCollateral>;
